import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import "./OwnerPortal.css";

function displayCount(value) {
  return Number.isInteger(value) && value >= 0 ? value.toLocaleString() : "Unavailable";
}

function OwnerPortal({ onClose }) {
  const { getOwnerOverview, grantComplimentaryAccess, revokeComplimentaryAccess, user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [complimentaryUserId, setComplimentaryUserId] = useState("");
  const [complimentaryReason, setComplimentaryReason] = useState("");
  const [complimentaryExpiresAt, setComplimentaryExpiresAt] = useState("");
  const [complimentaryMessage, setComplimentaryMessage] = useState("");
  const [complimentaryBusy, setComplimentaryBusy] = useState(false);

  useEffect(() => {
    let active = true;
    getOwnerOverview()
      .then((result) => {
        if (active) setOverview(result);
      })
      .catch((ownerError) => {
        if (active) setError(ownerError.message);
      });
    return () => {
      active = false;
    };
  }, [getOwnerOverview]);

  async function handleGrantComplimentaryAccess() {
    setComplimentaryBusy(true);
    setComplimentaryMessage("");

    try {
      const result = await grantComplimentaryAccess(
        complimentaryUserId.trim(),
        complimentaryReason.trim() || null,
        complimentaryExpiresAt.trim() ? new Date(complimentaryExpiresAt).toISOString() : null,
      );
      setComplimentaryMessage(
        result?.entitlement?.complimentary_full_access
          ? "Complimentary Full Access granted."
          : "Grant request completed.",
      );
    } catch (grantError) {
      setComplimentaryMessage(grantError.message);
    } finally {
      setComplimentaryBusy(false);
    }
  }

  async function handleRevokeComplimentaryAccess() {
    setComplimentaryBusy(true);
    setComplimentaryMessage("");

    try {
      const result = await revokeComplimentaryAccess(
        complimentaryUserId.trim(),
        complimentaryReason.trim() || null,
      );
      setComplimentaryMessage(
        result?.entitlement?.complimentary_full_access === false
          ? "Complimentary Full Access revoked."
          : "Revoke request completed.",
      );
    } catch (revokeError) {
      setComplimentaryMessage(revokeError.message);
    } finally {
      setComplimentaryBusy(false);
    }
  }
  const users = overview?.users;
  const roleBreakdown = users?.role_breakdown;
  const subscriptions = overview?.subscriptions;

  return (
    <div className="ownerPortalBackdrop" role="presentation">
      <section className="ownerPortal" role="dialog" aria-modal="true" aria-labelledby="owner-portal-title">
        <header>
          <div>
            <span>ANINATRADE OWNER ACCESS</span>
            <h2 id="owner-portal-title">Owner Portal</h2>
            <p>Founder-only platform overview using authenticated backend data.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close Owner Portal">×</button>
        </header>

        {error && <div className="ownerPortalError" role="alert">{error}</div>}
        {!error && !overview && <div className="ownerPortalLoading">Loading protected owner overview…</div>}
        {overview?.authorized && (
          <div className="ownerPortalGrid">
            <article><span>IDENTITY</span><strong>{user?.email}</strong><small>Authenticated Owner account</small></article>
            <article><span>TOTAL USERS</span><strong>{displayCount(users?.total_users)}</strong><small>Authenticated Supabase users</small></article>
            <article><span>REGULAR USERS</span><strong>{displayCount(roleBreakdown?.user)}</strong><small>Main-platform access</small></article>
            <article><span>ADMINS</span><strong>{displayCount(roleBreakdown?.admin)}</strong><small>Admin Portal access only</small></article>
            <article><span>OWNERS</span><strong>{displayCount(roleBreakdown?.owner)}</strong><small>Owner and Admin Portal access</small></article>
            <article>
              <span>SUBSCRIPTIONS</span>
              <strong>{subscriptions?.connected ? "Connected" : "Not connected"}</strong>
              <small>{subscriptions?.message || "Subscription status unavailable."}</small>
            </article>
            <article><span>AUDIT</span><strong>Recorded</strong><small>This access event was written server-side.</small></article>
            <article><span>SECURITY</span><strong>Owner only</strong><small>Counts are returned only after backend role verification.</small></article>
          </div>
        )}
        {overview?.authorized && (
          <section className="ownerPortalComplimentaryAccess" aria-labelledby="complimentary-access-title">
            <h3 id="complimentary-access-title">Complimentary Full Access</h3>
            <p>Owner-only access for approved investors, team members, advisors, family, or other trusted people.</p>

            <label>
              User ID
              <input
                type="text"
                value={complimentaryUserId}
                onChange={(event) => setComplimentaryUserId(event.target.value)}
                placeholder="Supabase user UUID"
                disabled={complimentaryBusy}
              />
            </label>

            <label>
              Reason
              <input
                type="text"
                value={complimentaryReason}
                onChange={(event) => setComplimentaryReason(event.target.value)}
                placeholder="Investor, Team, Advisor..."
                disabled={complimentaryBusy}
              />
            </label>

            <label>
              Optional expiration
              <input
                type="datetime-local"
                value={complimentaryExpiresAt}
                onChange={(event) => setComplimentaryExpiresAt(event.target.value)}
                disabled={complimentaryBusy}
              />
            </label>

            <div className="ownerPortalComplimentaryActions">
              <button type="button" onClick={handleGrantComplimentaryAccess} disabled={complimentaryBusy || !complimentaryUserId.trim()}>
                Grant Complimentary Full Access
              </button>
              <button type="button" onClick={handleRevokeComplimentaryAccess} disabled={complimentaryBusy || !complimentaryUserId.trim()}>
                Revoke Complimentary Full Access
              </button>
            </div>

            {complimentaryMessage && (
              <div className="ownerPortalComplimentaryMessage" role="status">
                {complimentaryMessage}
              </div>
            )}
          </section>
        )}
      </section>
    </div>
  );
}

export default OwnerPortal;




